import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IonModal, NavController, Platform } from '@ionic/angular';
import { Howl } from 'howler';
import { DateTime, Duration, Interval } from 'luxon';
import { AuthRepository } from 'src/app/auth/state/auth.repository';
import { DBS } from 'src/app/classes/database-manager.class';
import { LocalDb } from 'src/app/classes/local-db.class';
import { WorkoutClass } from 'src/app/classes/workout.class';
import { UserEffort } from 'src/app/interfaces/firebase/user-effort.interface';
import { UserTime } from 'src/app/interfaces/firebase/user-time.interface';
import { Week } from 'src/app/interfaces/week.interface';
import { Workout } from 'src/app/interfaces/workout.interface';
import { DataService } from 'src/app/services/data.service';
import { UserEffortService } from 'src/app/services/firebase/user-effort.service';
import { UserTimeService } from 'src/app/services/firebase/user-time.service';
import { environment } from 'src/environments/environment';
import Swal from 'sweetalert2';

@Component({
  templateUrl: './workout.page.html',
  styleUrls: ['./workout.page.css']
})
export class WorkoutPage {

  weekId: number = 0;
  workoutId: number = 0;
  day: number = null;

  week: Week
  workout: Workout

  dbWeeks = new LocalDb(DBS.Weeks)
  dbWorkouts = new LocalDb(DBS.Workouts)
  dbFinishedWorkout = new LocalDb(DBS.FinishedWorkouts)

  startAt: DateTime = null
  endAt: DateTime = null

  controls = false
  crossOrigin = false

  constructor(
    private route: ActivatedRoute,
    private dataService: DataService,
    private router: Router,
    private navCtrl: NavController,
    private authRep: AuthRepository,
    private userTimeService: UserTimeService,
    private userEffortService: UserEffortService,
    private platform: Platform,
  ) {
    this.controls = this.platform.is('ios') ? true : false
    this.crossOrigin = this.platform.is('ios') ? true : false

    // Get nav params
    this.weekId = Number(this.route.snapshot.params.week_id)
    this.workoutId = Number(this.route.snapshot.params.workout_id)
    this.extractStateParams()

    this.workout = new WorkoutClass()
    // Find data
    // this.week = this.getWeek(this.trainingType)
    const data = this.dataService.getData()
    this.week = data.filter(week => week.id == this.weekId).pop()
    // this.workout = this.dataService.findWorkout(this.workoutId);
    this.getData()
  }

  extractStateParams() {
    const state = this.router.getCurrentNavigation().extras.state
    if (!state) this.router.navigateByUrl('/weeks')
    this.day = state.day
  }

  navigateBack() {
    this.navCtrl.navigateBack(`/workouts/${this.weekId}`, {
      state: {
        day: this.day
      }
    })
  }

  ionViewDidEnter() {
    this.startAt = DateTime.now()
    this.endedWeek()
  }

  async endedWeek() {
    this.dataService.getExercisesNumberOnWeek(this.weekId)
  }

  async getData() {
    this.week = await <Week><unknown>this.dbWeeks.find(this.weekId)
    this.workout = await <Workout><unknown>this.dbWorkouts.find(this.workoutId)
  }

  async startRestTimer(seconds: number) {
    const MILISECONDS = 1000;
    const time = seconds * MILISECONDS

    let timerInterval = null;
    return Swal.fire({
      heightAuto: false,
      allowOutsideClick: false,
      allowEscapeKey: !environment.production,
      icon: 'success',
      title: 'Aproveite seu descanso 😀!',
      html: 'Em <b></b>s você será direcionado para o próximo exercício.',
      timer: time,
      timerProgressBar: true,
      didOpen: () => {
        Swal.showLoading()

        const b = Swal.getHtmlContainer().querySelector('b')
        timerInterval = setInterval(() => {
          const { seconds } = Duration.fromObject({ millisecond: Swal.getTimerLeft() })
            .shiftTo("seconds")
            .toObject()
          b.textContent = String(Math.round(seconds))

        }, 500)
      },
      willClose: () => {
        clearInterval(timerInterval)
      }
    })
  }

  endExercise() {
    Swal.fire({
      heightAuto: false,
      title: 'Finalizar exercício?',
      text: "Você tem certeza que deseja finalizar o exercício?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#2dd36f',
      cancelButtonColor: '#eb445a',
      confirmButtonText: 'Sim, finalizar!',
      cancelButtonText: 'Cancelar',
    }).then((result) => {
      if (result.value) {
        this.markAsDone(true)
      }
    })
  }

  failExercise() {
    Swal.fire({
      heightAuto: false,
      // title: 'Finalizar exercício?',
      text: "Você tem certeza que deseja abandonar o exercício?",
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#2dd36f',
      cancelButtonColor: '#eb445a',
      confirmButtonText: 'Sim, abandonar!',
      cancelButtonText: 'Cancelar',
    }).then(async (result) => {
      if (result.value) {
        this.markAsDone(false)
      }
    })
  }

  getRecord(finished: boolean): UserTime {
    this.endAt = DateTime.now()
    const interval = Interval.fromDateTimes(this.startAt, this.endAt)
    return {
      user_id: this.authRep.getState().user.id,
      user_email: this.authRep.getState().user.username,
      week_id: this.weekId,
      week_number: this.week.week_number,
      week_level: this.week.level,
      week_part: this.week.part,
      week_name: this.week.name,
      workout_id: this.workout.id,
      workout_name: this.workout.workout,
      day: this.day,
      date: new Date(),
      finished,
      start_at: this.startAt.toFormat("yyyy-MM-dd'T'HH:mm:ss"),
      end_at: this.endAt.toFormat("yyyy-MM-dd'T'HH:mm:ss"),
      duration: interval.toFormat("hh:mm:ss")
    }
  }

  async markAsDone(finished: boolean) {
    const payload = this.getRecord(finished)
    const result = await this.dbFinishedWorkout.save(payload)
    this.userTimeService.create(payload)

    if (await this.dataService.dayCompleted(this.weekId, this.day)) {
      this.modal.present()
    } else {
      if (finished) {
        if (this.workout.rest_time_in_sec == null) {
          this.navCtrl.navigateBack(`/workouts/${this.weekId}`)
        } else {
          this.startRestTimer(this.workout.rest_time_in_sec).then(() => {
            this.navCtrl.navigateBack(`/workouts/${this.weekId}`)
          })
        }
      } else {
        this.navCtrl.navigateBack(`/workouts/${this.weekId}`)
      }
    }
  }

  getWeek(trainingType) {
    const data = this.dataService.getData()
    switch (trainingType) {
      case 0: return data.filter(week => week.part == "INICIAL").pop();
      case 1: return data.filter(week => week.week_number == this.workoutId).pop();
      case 2: return data.filter(week => week.part == "FINAL").pop();
    }
  }

  startTimer() {

    var sound = new Howl({
      src: ['/assets/audio/mixkit-simple-countdown-922.wav'],
      html5: true,
      onend: () => { }
    });

    sound.play();
  }





  @ViewChild(IonModal) modal: IonModal;
  effortPerception: number = 0;

  cancel() {
    this.modal.dismiss(null, 'cancel');
  }

  confirm() {
    this.modal.canDismiss = true
    this.modal.dismiss({
      effortPerception: this.effortPerception
    }, 'confirm');
  }

  onWillDismiss(event: Event) {
    const ev = event as any;
    if (ev.detail.role === 'confirm') {
      this.saveEffortPerception(ev.detail.data.effortPerception)
    }
  }

  async saveEffortPerception(effortPerception: number) {
    const payload: UserEffort = {
      user_id: this.authRep.getState().user.id,
      user_email: this.authRep.getState().user.username,
      week_id: this.weekId,
      week_number: this.week.week_number,
      week_level: this.week.level,
      week_part: this.week.part,
      week_name: this.week.name,
      day: this.day,
      date: new Date(),
      effort: effortPerception
    }
    this.userEffortService.create(payload)
    this.navCtrl.navigateBack(`/weeks`)
  }

  onDidDismiss(event: Event) {
    const ev = event as any;
  }

}
